import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from "moment";
import PropTypes from "prop-types";
import ChartLegend from "./ChartLegend";

const categorizeRegistrations = (registrations = {}, timePeriod = "today") => {
  // For "today": show 24-hour time intervals
  // For other periods: show dates
  const isToday = timePeriod === "today";

  let categories = [];
  let nurseData = [];
  let patientData = [];
  let physiotherapistData = [];

  if (isToday) {
    // Create 24-hour time intervals for today
    categories = [...Array(24).keys()].map((hour) =>
      moment({ hour }).format("h A")
    );
    nurseData = new Array(24).fill(0);
    patientData = new Array(24).fill(0);
    physiotherapistData = new Array(24).fill(0);

    // Group by hour
    Object.keys(registrations).forEach((role) => {
      const list = registrations[role];
      if (Array.isArray(list)) {
        list.forEach((registration) => {
          const createdAt = moment(registration.createdAt);
          const hour = createdAt.hour();

          if (hour >= 0 && hour < 24) {
            switch (role) {
              case "nurseList":
                nurseData[hour]++;
                break;
              case "patientList":
                patientData[hour]++;
                break;
              case "physiotherapist":
                physiotherapistData[hour]++;
                break;
              default:
                break;
            }
          }
        });
      }
    });
  } else {
    // Group by date for other periods
    const dateMap = {};

    Object.keys(registrations).forEach((role) => {
      const list = registrations[role];
      if (Array.isArray(list)) {
        list.forEach((registration) => {
          const createdAt = moment(registration.createdAt);
          const dateKey = createdAt.format("MMM DD"); // Format: "Jan 23"

          if (!dateMap[dateKey]) {
            dateMap[dateKey] = { nurse: 0, patient: 0, physiotherapist: 0 };
          }

          switch (role) {
            case "nurseList":
              dateMap[dateKey].nurse++;
              break;
            case "patientList":
              dateMap[dateKey].patient++;
              break;
            case "physiotherapist":
              dateMap[dateKey].physiotherapist++;
              break;
            default:
              break;
          }
        });
      }
    });

    // Sort dates chronologically and create arrays
    const sortedDates = Object.keys(dateMap).sort((a, b) => {
      return moment(a, "MMM DD").valueOf() - moment(b, "MMM DD").valueOf();
    });

    categories = sortedDates;
    nurseData = sortedDates.map((date) => dateMap[date].nurse);
    patientData = sortedDates.map((date) => dateMap[date].patient);
    physiotherapistData = sortedDates.map(
      (date) => dateMap[date].physiotherapist
    );
  }

  return { categories, nurseData, patientData, physiotherapistData };
};

const Role = ({ registrations, hideLegend = false, timePeriod = "today" }) => {
  const { nurseList, patientList, physiotherapist } = registrations;
  const { categories, nurseData, patientData, physiotherapistData } =
    categorizeRegistrations(
      {
        nurseList,
        patientList,
        physiotherapist,
      },
      timePeriod
    );
  // console.log(patientList.length, nurseList.length);
  const show = nurseList.length > 0 || patientList.length > 0;

  // console.log(show);
  const options = {
    chart: {
      type: "line",
      height: 150,
      backgroundColor: "transparent",
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: categories, // Dynamic categories based on time period
      lineColor: "#D7E4EB",
      lineWidth: 1,
      tickColor: "#333",
      gridLineColor: "#C4CDD2",
      gridLineDashStyle: "dot",
      gridLineWidth: 1.5,
      labels: {
        style: {
          color: "#474747",
          fontSize: "10px",
        },
      },
    },
    yAxis: {
      title: {
        text: "",
      },
      allowDecimals: false,
      gridLineColor: "#fff",
      labels: {
        style: {
          color: "#474747",
          fontSize: "10px",
        },
      },
    },
    plotOptions: {
      line: {
        dataLabels: {
          enabled: true,
          // Only show labels for non-zero values
          formatter: function () {
            return this.y === 0 ? null : this.y;
          },
          style: {
            fontSize: "8px",
            color: "#000",
          },
        },
        enableMouseTracking: true,
      },
      series: {
        marker: {
          enabled: true,
          // Hide markers for zero values
          states: {
            normal: {
              enabled: true,
            },
          },
        },
      },
    },
    series: [
      {
        name: "Nurse",
        data: nurseData,
        color: "#A4E472",
        lineWidth: 2,
        marker: {
          enabled: true,
          radius: 3,
          symbol: "circle",
          // Only show markers for non-zero values
          enabledThreshold: 0,
        },
      },
      {
        name: "Patient",
        data: patientData,
        color: "#A3C8F4",
        lineWidth: 2,
        marker: {
          enabled: true,
          radius: 3,
          symbol: "circle",
          enabledThreshold: 0,
        },
      },
      {
        name: "Physiotherapist",
        data: physiotherapistData,
        color: "#9DAEF4",
        lineWidth: 2,
        marker: {
          enabled: true,
          radius: 3,
          symbol: "circle",
          enabledThreshold: 1, // Only show markers for values >= 1 (hide zeros)
        },
      },
    ],
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
  };

  const legendItems = [
    { label: "Nurse", color: "#A4E472" },
    { label: "Patient", color: "#A3C8F4" },
    { label: "Physiotherapist", color: "#9DAEF4" },
  ];

  return (
    <div
      className={`bg-white h-[35vh] shadow-md ${
        hideLegend ? "rounded-xl" : "rounded-xl"
      }`}
    >
      <div className="flex justify-between items-center px-5 pt-4">
        <p className="text-base font-bold text-gray-800">Users By Role</p>
      </div>
      <div className="border-b border-gray-200 mb-3 mt-3"></div>
      {!hideLegend && <ChartLegend items={legendItems} />}
      <div className="px-4">
        {show ? (
          <HighchartsReact highcharts={Highcharts} options={options} />
        ) : (
          <div className="flex justify-center items-center h-40">
            <p className="text-gray-500 text-[14px]">
              No new registrations in selected period
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

Role.propTypes = {
  registrations: PropTypes.shape({
    nurseList: PropTypes.array,
    patientList: PropTypes.array,
    physiotherapist: PropTypes.array,
  }).isRequired,
  hideLegend: PropTypes.bool,
  timePeriod: PropTypes.string,
};

export default Role;
